/* eslint-disable prefer-destructuring */

import React, { ReactNode, SyntheticEvent, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core';
import red from '@material-ui/core/colors/red';
import orange from '@material-ui/core/colors/orange';
import yellow from '@material-ui/core/colors/yellow';
import green from '@material-ui/core/colors/green';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import { SurveyData, PatientData } from '../generated-api';
import PromisHistoryPopover from './PromisHistoryPopover';

export interface PromisScoreProps {
  score: number;
  change?: boolean;
  type: 'pain' | 'function';
  showCategory?: boolean;
  /**
   * If given, this will show a popover of the history of the patient's PROMIS
   * scores when clicked on.
   */
  patientHistory?: PatientData;
  classes?: Partial<ReturnType<typeof useStyles>>;
}

export default function PromisScore(props: PromisScoreProps) {
  const {
    score, change, type, patientHistory, showCategory,
  } = props;

  const classes = useStyles(props);
  const theme = useTheme();

  const hasPopover = patientHistory != null;

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const handleClick = (event: SyntheticEvent) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const popoverOpen = Boolean(anchorEl);
  const id = popoverOpen ? 'component-popover' : undefined;

  const scoreFormat = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
  });

  let icon: ReactNode = null;
  let category: string | null = null;
  if (change) {
    let improveIcon: ReactNode;
    let worsenIcon: ReactNode;
    if (type === 'pain') {
      improveIcon = <TrendingDownIcon />;
      worsenIcon = <TrendingUpIcon />;
    } else {
      improveIcon = <TrendingUpIcon />;
      worsenIcon = <TrendingDownIcon />;
    }

    let color: string;
    if (
      (type === 'pain' && score < -0.5)
      || (type === 'function' && score > 0.5)
    ) {
      icon = improveIcon;
      color = green[500];
    } else if (
      (type === 'pain' && score > 0.5)
      || (type === 'function' && score < -0.5)
    ) {
      icon = worsenIcon;
      color = red[500];
    } else {
      icon = <TrendingFlatIcon />;
      color = orange[500];
    }
    icon = (
      <span className={classes.changeIcon} style={{ color }}>{icon}</span>
    );
  } else {
    const normalizedScore = type === 'pain' ? 100 - score : score;
    let color: string;
    if (normalizedScore >= 45) {
      color = green[500];
      category = 'Normal';
    } else if (normalizedScore >= 40) {
      color = yellow[500];
      category = 'Mild';
    } else if (normalizedScore >= 30) {
      color = orange[500];
      category = 'Moderate';
    } else {
      color = red[500];
      category = 'Severe';
    }

    icon = (
      <span className={classes.scoreIcon} style={{ color }}>●</span>
    );
  }

  return (
    <>
      <span
        aria-describedby={id}
        onClick={hasPopover ? handleClick : undefined}
        onKeyUp={hasPopover ? (event) => {
          if ([13, 32].includes(event.which)) handleClick(event);
        } : undefined}
        role={hasPopover ? 'button' : undefined}
        tabIndex={hasPopover ? 0 : undefined}
        className={hasPopover ? classes.clickable : undefined}
        title={hasPopover ? 'Click to view PROMIS® score history' : undefined}
      >
        {scoreFormat.format(score)}
        {icon}
        {showCategory && category != null && (
          <span className={classes.category}>
            {category}
          </span>
        )}
      </span>
      {patientHistory != null && (
        <PromisHistoryPopover
          id={id}
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          type={type}
          patient={patientHistory}
        />
      )}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  scoreIcon: {
    marginLeft: theme.spacing(1),
    fontSize: 24,
    lineHeight: '14px',
    position: 'relative',
    top: 2,
  },
  changeIcon: {
    marginLeft: theme.spacing(1),
    '& svg': {
      position: 'relative',
      top: 'calc(1px + 0.15em)',
      marginTop: 'calc(-1px - 0.15em)',
      width: '1.3em',
      height: '1.3em',
      fontSize: 'inherit',
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  category: {
  },
}));
